/* ============================================================================
   Obsidyx on Body — Gallery interactions
   ----------------------------------------------------------------------------
   Web Component that powers the masonry gallery + native <dialog> lightbox.
   - Fades items in on first scroll-into-view
   - Click/Enter on item opens lightbox at that index
   - ArrowLeft/Right navigate, Escape closes
   - Touch swipe (left/right) navigates on mobile
   - Restores focus to the originating tile on close
   ========================================================================== */

(function () {
  if (window.customElements.get('obsidyx-gallery')) return;

  const PREFERS_REDUCED_MOTION = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;

  class ObsidyxGallery extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-gallery-item]'));
      this.dialog = this.querySelector('[data-lightbox]');
      this.imgEl = this.querySelector('[data-lightbox-image]');
      this.captionEl = this.querySelector('[data-lightbox-caption]');
      this.counterEl = this.querySelector('[data-lightbox-counter]');
      this.prevBtn = this.querySelector('[data-lightbox-prev]');
      this.nextBtn = this.querySelector('[data-lightbox-next]');
      this.closeBtn = this.querySelector('[data-lightbox-close]');
      this.currentIndex = 0;
      this.lastFocused = null;

      this._bindEvents();
      this._observeReveal();
    }

    _bindEvents() {
      this.items.forEach((item, idx) => {
        item.addEventListener('click', () => this.open(idx, item));
      });

      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.prev());
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.next());
      if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());

      if (this.dialog) {
        // Keyboard navigation while open
        this.dialog.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight') { e.preventDefault(); this.next(); }
          else if (e.key === 'ArrowLeft') { e.preventDefault(); this.prev(); }
        });

        // Click on backdrop closes
        this.dialog.addEventListener('click', (e) => {
          if (e.target === this.dialog) this.close();
        });

        this.dialog.addEventListener('close', () => this._restoreFocus());

        // Touch swipe
        let touchStartX = null;
        this.dialog.addEventListener('touchstart', (e) => {
          touchStartX = e.changedTouches[0].screenX;
        }, { passive: true });
        this.dialog.addEventListener('touchend', (e) => {
          if (touchStartX == null) return;
          const dx = e.changedTouches[0].screenX - touchStartX;
          if (Math.abs(dx) > 40) {
            if (dx < 0) this.next();
            else this.prev();
          }
          touchStartX = null;
        }, { passive: true });
      }
    }

    _observeReveal() {
      if (PREFERS_REDUCED_MOTION || !('IntersectionObserver' in window)) {
        this.items.forEach((el) => el.classList.add('is-visible'));
        return;
      }

      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              // Stagger by index for a soft cascade (max 12 * 50ms)
              const i = Number(entry.target.dataset.index || 0) % 12;
              entry.target.style.transitionDelay = `${i * 50}ms`;
              entry.target.classList.add('is-visible');
              io.unobserve(entry.target);
            }
          });
        },
        { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
      );

      this.items.forEach((el) => io.observe(el));
    }

    open(index, originEl) {
      this.lastFocused = originEl || document.activeElement;
      this.currentIndex = index;
      this._render();
      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else {
        // Fallback for very old browsers without <dialog>
        this.dialog.setAttribute('open', '');
        this.dialog.style.display = 'block';
      }
      document.body.style.overflow = 'hidden';
    }

    close() {
      if (typeof this.dialog.close === 'function') {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
        this.dialog.style.display = 'none';
      }
      document.body.style.overflow = '';
    }

    _restoreFocus() {
      if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
        this.lastFocused.focus();
      }
    }

    next() { this._step(1); }
    prev() { this._step(-1); }

    _step(delta) {
      const len = this.items.length;
      this.currentIndex = (this.currentIndex + delta + len) % len;
      this._render();
    }

    _render() {
      const item = this.items[this.currentIndex];
      if (!item) return;
      const fullSrc = item.dataset.fullSrc;
      const caption = item.dataset.caption || '';
      const innerImg = item.querySelector('img');
      const altText = innerImg ? innerImg.alt : `Image ${this.currentIndex + 1}`;

      // Set src & restart entrance animation by reflowing
      this.imgEl.src = fullSrc;
      this.imgEl.alt = altText;
      this.imgEl.style.animation = 'none';
      // eslint-disable-next-line no-unused-expressions
      this.imgEl.offsetWidth;
      this.imgEl.style.animation = '';

      this.captionEl.textContent = caption;
      this.counterEl.textContent = `${this.currentIndex + 1} / ${this.items.length}`;
    }
  }

  window.customElements.define('obsidyx-gallery', ObsidyxGallery);
})();
